import { FileText, Download, AlertCircle, Calendar } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

interface ControlledDrug {
  id: number
  date: string
  ticket: string
  product: string
  quantity: number
  customer: string
  prescription: string
  seller: string
}

interface Antibiotic {
  id: number
  date: string
  ticket: string
  product: string
  quantity: number
  customer: string
  prescription: string
  doctor: string
  seller: string
}

const controlledDrugs: ControlledDrug[] = [
  {
    id: 1,
    date: '2026-04-27',
    ticket: 'VT-1245',
    product: 'Clonazepam 2mg',
    quantity: 30,
    customer: 'María López García',
    prescription: 'RX-2024-5678',
    seller: 'Carlos Méndez',
  },
  {
    id: 2,
    date: '2026-04-26',
    ticket: 'VT-1238',
    product: 'Alprazolam 0.5mg',
    quantity: 20,
    customer: 'Juan Pérez Soto',
    prescription: 'RX-2024-5645',
    seller: 'Ana Torres',
  },
  {
    id: 3,
    date: '2026-04-25',
    ticket: 'VT-1220',
    product: 'Tramadol 50mg',
    quantity: 15,
    customer: 'Patricia Silva',
    prescription: 'RX-2024-5612',
    seller: 'Carlos Méndez',
  },
]

const antibiotics: Antibiotic[] = [
  {
    id: 1,
    date: '2026-04-27',
    ticket: 'VT-1242',
    product: 'Amoxicilina 500mg',
    quantity: 21,
    customer: 'Luis Ramírez',
    prescription: 'RX-2024-5680',
    doctor: 'Dr. Hernández',
    seller: 'María López',
  },
  {
    id: 2,
    date: '2026-04-26',
    ticket: 'VT-1235',
    product: 'Azitromicina 500mg',
    quantity: 6,
    customer: 'Carmen Ortiz',
    prescription: 'RX-2024-5672',
    doctor: 'Dra. González',
    seller: 'Ana Torres',
  },
  {
    id: 3,
    date: '2026-04-25',
    ticket: 'VT-1228',
    product: 'Ciprofloxacino 500mg',
    quantity: 14,
    customer: 'Roberto Díaz',
    prescription: 'RX-2024-5658',
    doctor: 'Dr. Martínez',
    seller: 'Carlos Méndez',
  },
]

export function Regulatory() {
  const [startDate, setStartDate] = useState('2026-04-01')
  const [endDate, setEndDate] = useState('2026-04-27')

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          Módulo Regulatorio
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Reportes de medicamentos controlados y antibióticos
        </p>
      </div>

      {/* Important Notice */}
      <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <h3 className="font-medium text-primary/90">
              Cumplimiento Regulatorio
            </h3>
            <p className="mt-1 text-sm text-primary">
              Todos los registros deben conservarse por al menos 5 años según la
              normativa COFEPRIS. Asegúrese de exportar y respaldar estos
              reportes regularmente.
            </p>
          </div>
        </div>
      </div>

      {/* Date Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-col items-end gap-4 md:flex-row">
          <div className="flex-1 space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Fecha inicio:
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex-1 space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Fecha fin:
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <Button>
            <Calendar className="mr-2 h-4 w-4" />
            Aplicar Filtro
          </Button>
        </div>
      </div>

      <Tabs defaultValue="controlled" className="space-y-6">
        <TabsList>
          <TabsTrigger value="controlled">Medicamentos Controlados</TabsTrigger>
          <TabsTrigger value="antibiotics">Antibióticos</TabsTrigger>
          <TabsTrigger value="export">Exportar Reportes</TabsTrigger>
        </TabsList>

        {/* Controlled Drugs */}
        <TabsContent value="controlled" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-slate-900">
                Registro de Medicamentos Controlados
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {controlledDrugs.length} registros en el periodo seleccionado
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Exportar a Excel
              </Button>
              <Button variant="outline">
                <FileText className="mr-2 h-4 w-4" />
                Exportar a PDF
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Fecha de venta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Ticket
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cantidad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Receta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Vendedor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {controlledDrugs.map((drug) => (
                    <tr key={drug.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {drug.date}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-slate-900">
                        {drug.ticket}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-900">
                        {drug.product}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {drug.quantity}
                      </td>
                      <td className="px-4 py-4">
                        <Badge className="bg-red-100 text-red-700">
                          {drug.prescription}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {drug.seller}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Antibiotics */}
        <TabsContent value="antibiotics" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-slate-900">
                Registro de Antibióticos
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                {antibiotics.length} registros en el periodo seleccionado
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Exportar a Excel
              </Button>
              <Button variant="outline">
                <FileText className="mr-2 h-4 w-4" />
                Exportar a PDF
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Fecha de venta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Ticket
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cantidad
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Receta
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Médico
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                      Vendedor
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {antibiotics.map((antibiotic) => (
                    <tr key={antibiotic.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {antibiotic.date}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-slate-900">
                        {antibiotic.ticket}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-900">
                        {antibiotic.product}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {antibiotic.quantity}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {antibiotic.customer}
                      </td>
                      <td className="px-4 py-4">
                        <Badge className="bg-orange-100 text-orange-700">
                          {antibiotic.prescription}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {antibiotic.doctor}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {antibiotic.seller}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Export Reports */}
        <TabsContent value="export" className="space-y-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-red-50 p-3">
                  <FileText className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Medicamentos Controlados
                  </h3>
                  <p className="text-sm text-slate-600">
                    Reporte oficial COFEPRIS
                  </p>
                </div>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Formato oficial para reporte trimestral de medicamentos
                controlados según normativa vigente.
              </p>
              <Button className="w-full" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Generar Reporte Excel
              </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-orange-50 p-3">
                  <FileText className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Antibióticos</h3>
                  <p className="text-sm text-slate-600">
                    Reporte para autoridades
                  </p>
                </div>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Reporte de dispensación de antibióticos con datos completos de
                prescripción y médico tratante.
              </p>
              <Button className="w-full" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Generar Reporte Excel
              </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Historial de Auditoría
                  </h3>
                  <p className="text-sm text-slate-600">Registro de acciones</p>
                </div>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Log completo de todas las operaciones realizadas en el módulo
                regulatorio para trazabilidad.
              </p>
              <Button className="w-full" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Generar Reporte PDF
              </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-green-50 p-3">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Reporte Consolidado
                  </h3>
                  <p className="text-sm text-slate-600">
                    Todos los productos regulados
                  </p>
                </div>
              </div>
              <p className="mb-4 text-sm text-slate-600">
                Reporte consolidado que incluye todos los medicamentos
                controlados y antibióticos en un solo archivo.
              </p>
              <Button className="w-full" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Generar Reporte Excel
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
